import type { LngLatBoundsLike } from 'mapbox-gl';
import type { Deal } from './types';
import type { DealColorLookup } from './MapView/hooks/useColoredDealIds';
import { CONFLICT_COLOR } from './MapView/components/mapTheme';
import { DEAL_GROUP_PALETTE } from './constants';

export interface DealCoordinates {
    lat: number;
    lng: number;
}

const formatDealAddressLine = (deal: Deal): string => [deal.address, deal.city, deal.state, deal.zipCode]
    .filter(value => value != null && value !== '')
    .join(' ');

export const getDealCoords = (
    deal: Deal,
    geocodedCoordinates: Record<number, DealCoordinates>,
): DealCoordinates | null => {
    if (deal.latitude != null && deal.longitude != null) {
        return { lat: deal.latitude, lng: deal.longitude };
    }
    return geocodedCoordinates[deal.id] ?? null;
};

export const buildGeoJSON = (
    deals: Deal[],
    geocodedCoordinates: Record<number, DealCoordinates>,
    colorByDealId: DealColorLookup = {},
): GeoJSON.FeatureCollection<GeoJSON.Point> => ({
    type: 'FeatureCollection',
    features: deals
        .map(deal => {
            const coords = getDealCoords(deal, geocodedCoordinates);
            if (!coords) return null;
            const colorEntry = colorByDealId[deal.id];
            const properties: Record<string, string | number> = {
                id: deal.id,
                dealName: deal.dealName,
                addressLine: formatDealAddressLine(deal),
            };
            let groupColorIdx = -1;
            if (colorEntry) {
                properties.colorHex = colorEntry.hexColor;
                if (colorEntry.hexColor === CONFLICT_COLOR) {
                    properties.conflictGroups = colorEntry.groupNames.join(', ');
                }
                groupColorIdx = DEAL_GROUP_PALETTE.findIndex(entry => colorEntry.hexColor === entry.hex);
            }
            properties.groupColorIdx = groupColorIdx;
            DEAL_GROUP_PALETTE.forEach((_, idx) => {
                properties[`groupColor_${idx}`] = groupColorIdx === idx ? 1 : 0;
            });
            return {
                type: 'Feature' as const,
                properties,
                geometry: {
                    type: 'Point' as const,
                    coordinates: [coords.lng, coords.lat],
                },
            };
        })
        .filter((f): f is NonNullable<typeof f> => f != null),
});

export const getBoundsFromGeoJSON = (
    geojson: GeoJSON.FeatureCollection<GeoJSON.Point>,
): LngLatBoundsLike | null => {
    if (geojson.features.length === 0) return null;

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    geojson.features.forEach(feature => {
        const [lng, lat] = feature.geometry.coordinates;
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
    });

    return [[minLng, minLat], [maxLng, maxLat]];
};
